import { error, log } from '@/services/Log'
import { getCustomItems } from '@/helpers/checkoutHelpers'
import { standardizeCart } from '@/services/Standardizers/standardizeCart'
import type {
  StandardCart, StandardOrder, StandardLineItem, StandardOrderDiscount,
} from '@/types/ShopFront/CheckoutStandards'
import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'

type MinimalPhysiscalItem = {
  name: string,
  id: string,
  quantity: number,
  sku :string,
  discounts: readonly {
    discountedAmount: number
    id: string
  }[]
}

type AdditionalItem = {
  listPrice: number,
  id: string,
  quantity: number,
  sku :string
}

export type CartExtractorFromOrder = (
  cartIn: {
    baseAmount: number,
    cartAmount: number,
    discountAmount: number,
    email?: null | string,
    id:string,
    updatedTime: string,
    coupons: readonly StandardOrderDiscount[]
    customerId: number,
    lineItems: {
      physicalItems: readonly MinimalPhysiscalItem[],
      customItems: readonly AdditionalItem[],
      digitalItems: readonly AdditionalItem[]
    }
  },
  order: StandardOrder
) => StandardCart

export const ListrakTrackCart = (rawCart: StandardCart) => {
  allPromisesWithRetries(() => [import('@/services/Listrak/submitCart')])
    .then(([{ submitCart }]) => submitCart(rawCart))
    .catch(error)
}

type skuToOriginalMap = {
  [sku:string]: StandardLineItem
}

const getInitialSkuMap = (): skuToOriginalMap => ({})
const furnitureLast = (item1: StandardLineItem, item2: StandardLineItem) => {
  if (!item1.isFurniture && item2.isFurniture) {
    return -1
  }
  return (
    (item1.isFurniture && !item2.isFurniture)
      ? 1
      : 0
  )
}
export const getCartFromResponse: CartExtractorFromOrder = (rawCart, order) => {
  // Retrieving  product info from original order to avoid requests for item details
  log('getCartFromResponse Start', { rawCart, order })
  const map = order.cart.lineItems.reduce((acc, item) => ({
    ...acc,
    [item.sku]: item,
  }), getInitialSkuMap())

  // These props originally come from individual api requests in generate LineItemsFromCheckout
  // Here we take the previous item and overwrite the props from the new cart item
  const toStandardFromOriginal = ({
    id, quantity, sku, name, discounts,
  }: MinimalPhysiscalItem): StandardLineItem => ({
    ...map[sku],
    discounts: [...discounts],
    couponAmount: discounts.reduce((acc, { discountedAmount }) => acc + discountedAmount, 0),
    name: (map[sku]?.isDigital) ? map[sku].name : name,
    id,
    quantity,
  })
  const lineItems: StandardLineItem[] = (
    rawCart?.lineItems?.physicalItems
      .map(toStandardFromOriginal)
      .sort(furnitureLast)
  )
  const customItems = getCustomItems({
    lineItems,
    customItems: rawCart?.lineItems?.customItems,
  })
  log('getCartFromResponse End', { lineItems })
  const standardCart = standardizeCart({
    ...rawCart,
    email: rawCart.email || '',
    lineItems: [...lineItems, ...customItems, ...rawCart?.lineItems?.digitalItems],
  })
  ListrakTrackCart(standardCart)
  return standardCart
}

export default getCartFromResponse
